import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { InlineMentionData } from '@gitbook/doc-core';
import type { Inline } from '@gitbook/slate';
import type { EditorContainer, InlineProps } from '@gitbook/slate-react';
import { Link, LoadingIndicator, renderIcon } from '@gitbook/spine-react';

import { type UIResolvedContentReference, useContentReference } from '../../ContentReferences';
import { EditLinkWrapper } from '../link/EditLinkWrapper';
import { useInlineLinkProps } from '../link/useInlineLinkProp';

/**
 * Component to render an inline mention.
 */
export function InlineMention(props: InlineProps): React.ReactElement {
    const { attributes, isSelected } = props;

    return (
        <React.Suspense
            fallback={<InlineMentionLoading selected={isSelected} attributes={attributes} />}
        >
            <InlineMentionComponent {...props} />
        </React.Suspense>
    );
}

/** Fallback when loading the reference */
function InlineMentionLoading(props: {
    selected: boolean;
    attributes: object;
}): React.ReactElement {
    return (
        <span className="inline-flex select-none align-middle" {...props.attributes}>
            <LoadingIndicator size="inherit" />
        </span>
    );
}

/** Inner rendering of the reference, it may suspend rendering */
function InlineMentionComponent(props: InlineProps): React.ReactElement {
    const { node, editor, parent, attributes } = props;

    const nodeData = node.data.toFrozenObject() as InlineMentionData;
    const resolved = useContentReference(nodeData.ref);
    const inlineLinkProps = useInlineLinkProps(resolved);

    const wrapperClassName = clsx(
        'relative inline-block pl-[0.0625rem] align-middle',
        !editor.readOnly && '-top-[0.03125rem] leading-6'
    );

    return (
        <>
            {editor.readOnly ? (
                <span className={wrapperClassName}>
                    <InlineMentionContent
                        reference={nodeData.ref}
                        resolved={resolved}
                        attributes={attributes}
                        editor={props.editor}
                        node={node}
                    />
                </span>
            ) : (
                <EditLinkWrapper
                    kind="reference"
                    linkProps={inlineLinkProps}
                    resolved={resolved}
                    editor={editor}
                    node={node}
                    parent={parent}
                >
                    <span className={wrapperClassName}>
                        <InlineMentionContent
                            reference={nodeData.ref}
                            resolved={resolved}
                            attributes={attributes}
                            editor={props.editor}
                            node={node}
                        />
                    </span>
                </EditLinkWrapper>
            )}
        </>
    );
}

/** Inner rendering of the reference, it may suspend rendering */
function InlineMentionContent(props: {
    reference: api.ContentRef;
    resolved: UIResolvedContentReference;
    attributes: object;
    editor: EditorContainer;
    node: Inline;
    voidInlineClassName?: string;
}): React.ReactElement {
    const { reference, resolved, attributes, editor, node, voidInlineClassName } = props;

    const contentRefClassName = clsx(
        'relative mx-0.25 flex cursor-default items-center gap-1 text-wrap rounded bg-[var(--sp-color-bg-mention-default)] px-1 text-primary text-ui-base leading-6 no-underline'
    );

    if (reference.kind === 'user') {
        return (
            <span
                data-mention
                {...attributes}
                className={clsx(voidInlineClassName, contentRefClassName)}
            >
                {renderIcon(resolved.icon || resolved.secondaryIcon)}
                <span>@{resolved.title}</span>
            </span>
        );
    }

    const icon =
        ['page', 'space'].includes(reference.kind) && resolved.icon
            ? resolved.icon
            : resolved.secondaryIcon;

    const content = (
        <span className="relative flex h-full items-center gap-2">
            <span className="flex items-center align-middle">
                {renderIcon(icon, {
                    size: 14,
                    className: 'h-3.5 shrink-0 text-inherit',
                })}
            </span>

            <span>{resolved.title.trim()}</span>
        </span>
    );

    if (resolved.href) {
        return (
            <Link
                data-mention
                href={resolved.href}
                onPress={() => {
                    resolved.open();

                    editor.change((change) => {
                        change.collapseToStartOf(node).focus();
                    });
                }}
                className={clsx(voidInlineClassName, contentRefClassName)}
                {...attributes}
            >
                {content}
            </Link>
        );
    }

    return (
        <span
            data-mention
            className={clsx(voidInlineClassName, contentRefClassName)}
            {...attributes}
        >
            {content}
        </span>
    );
}
